import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth.tsx';
import { supabase } from '../lib/db.ts';
import { useSettings } from '../lib/settings/settingsContext.tsx';
import { 
  Key, 
  Eye, 
  EyeOff, 
  Copy, 
  Trash2, 
  Plus,
  Activity,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2,
  Calendar,
  Server,
  Zap
} from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ConfirmationModal } from '../components/common/ConfirmationModal.tsx';
import { BookOpen, Code2, Rocket, Layout, Home, ChevronRight } from 'lucide-react';

interface ApiKey {
  id: string;
  key_name: string;
  environment: 'test' | 'production';
  is_active: boolean;
  created_at: string;
  last_used_at: string | null;
  total_requests: number;
  requests_last_30_days: number;
  last_request_at: string | null;
}

interface UsageStats {
  total_requests: number;
  successful_requests: number;
  failed_requests: number;
  avg_response_time_ms: number;
  total_data_transferred_bytes: number;
  requests_by_endpoint: Array<{
    endpoint: string;
    count: number;
    avg_response_time_ms: number;
  }>;
  requests_by_day: Array<{
    date: string;
    count: number;
  }>;
}

export function DeveloperDashboard() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedKeyId, setSelectedKeyId] = useState<string | null>(null);
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [accountStatus, setAccountStatus] = useState<string | null>(null);
  const [keyToRevoke, setKeyToRevoke] = useState<string | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);
  const [commissionRate, setCommissionRate] = useState<number | null>(null);

  useEffect(() => {
    if (user) {
      checkAccountStatus();
      fetchApiKeys();
    }
  }, [user]);

  const checkAccountStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('developer_accounts')
        .select('status')
        .eq('user_id', user?.id)
        .single();

      if (data) {
        setAccountStatus(data.status);
      }
    } catch (err) {
      // Account doesn't exist or error
    }
  };

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_developer_api_keys_with_stats');

      if (error) throw error;

      if (data && data.success) {
        setApiKeys(data.api_keys || []);
        setCommissionRate(data.commission_rate || null);
      } else {
        throw new Error(data?.error || 'Failed to fetch API keys');
      }
    } catch (err) {
      console.error('Error fetching API keys:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageStats = async (apiKeyId: string) => {
    try {
      setLoadingStats(true);
      const { data, error } = await supabase.rpc('get_api_key_usage_stats', {
        p_api_key_id: apiKeyId,
        p_start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        p_end_date: new Date().toISOString()
      });

      if (error) throw error;

      if (data && data.success) {
        setUsageStats(data.stats);
      } else {
        throw new Error(data?.error || 'Failed to fetch usage statistics');
      }
    } catch (err) {
      console.error('Error fetching usage stats:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to load usage statistics');
    } finally {
      setLoadingStats(false);
    }
  };

  const handleRevokeClick = (apiKeyId: string) => {
    setKeyToRevoke(apiKeyId);
  };

  const revokeApiKey = async () => {
    if (!keyToRevoke) return;

    setIsRevoking(true);
    try {
      const { data, error } = await supabase.rpc('revoke_developer_api_key', {
        p_api_key_id: keyToRevoke
      });

      if (error) throw error;

      if (data && data.success) {
        toast.success('API key revoked successfully');
        fetchApiKeys();
        if (selectedKeyId === keyToRevoke) {
          setSelectedKeyId(null);
          setUsageStats(null);
        }
        setKeyToRevoke(null);
      } else {
        throw new Error(data?.error || 'Failed to revoke API key');
      }
    } catch (err) {
      console.error('Error revoking API key:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to revoke API key');
    } finally {
      setIsRevoking(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Required</h2>
          <p className="text-gray-600 mb-4">Please log in to access your developer dashboard.</p>
          <Link
            to="/auth/login?redirect=/developers/dashboard"
            className="inline-flex items-center px-4 py-2 bg-wayja-teal text-white rounded-lg hover:bg-wayja-dark-teal"
          >
            Log In
          </Link>
        </div>
      </div>
    );
  }

  if (accountStatus === null || accountStatus === 'pending') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {accountStatus === 'pending' ? 'Account Pending Approval' : 'Developer Account Required'}
          </h2>
          <p className="text-gray-600 mb-4">
            {accountStatus === 'pending' 
              ? 'Your developer account is currently under review. You will receive an email once it has been approved.'
              : 'You need to register as a developer to access the dashboard.'}
          </p>
          <Link
            to="/developers/register"
            className="inline-flex items-center px-4 py-2 bg-wayja-teal text-white rounded-lg hover:bg-wayja-dark-teal"
          >
            {accountStatus === 'pending' ? 'View Registration' : 'Register as Developer'}
          </Link>
        </div>
      </div>
    );
  }

  if (accountStatus !== 'approved') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Account Not Approved</h2>
          <p className="text-gray-600 mb-4">
            Your developer account has been {accountStatus}. Please contact support@wayja.me for more information.
          </p>
          <Link
            to="/developers/register"
            className="inline-flex items-center px-4 py-2 bg-wayja-teal text-white rounded-lg hover:bg-wayja-dark-teal"
          >
            View Account Status
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumbs */}
        <nav className="mb-6 flex items-center space-x-2 text-sm text-gray-600">
          <Link to="/" className="hover:text-wayja-teal flex items-center">
            <Home className="w-4 h-4 mr-1" />
            Home
          </Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/developers" className="hover:text-wayja-teal">
            Developers
          </Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-gray-900 font-medium">Dashboard</span>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Developer Dashboard</h1>
              <p className="text-gray-600">Manage your API keys and view usage statistics</p>
            </div>
            <Link
              to="/developers/register"
              className="inline-flex items-center px-4 py-2 bg-wayja-teal text-white rounded-lg hover:bg-wayja-dark-teal"
            >
              <Plus className="w-5 h-5 mr-2" />
              Generate New API Key
            </Link>
          </div>
        </div>

        {/* Developer Resources Navigation */}
        <div className="mb-6 bg-white rounded-lg shadow p-4">
          <h2 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Developer Resources</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Link
              to="/developers/quickstart"
              className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm text-gray-700 hover:text-wayja-teal"
            >
              <Rocket className="w-4 h-4" />
              <span>Quick Start</span>
            </Link>
            <Link
              to="/developers/api-reference"
              className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm text-gray-700 hover:text-wayja-teal"
            >
              <BookOpen className="w-4 h-4" />
              <span>API Reference</span>
            </Link>
            <Link
              to="/developers/examples"
              className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm text-gray-700 hover:text-wayja-teal"
            >
              <Code2 className="w-4 h-4" />
              <span>Code Examples</span>
            </Link>
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg opacity-60 cursor-not-allowed text-sm text-gray-400 relative">
              <Layout className="w-4 h-4" />
              <span>Widget SDK</span>
              <span className="ml-1 px-1.5 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                Soon
              </span>
            </div>
            <Link
              to="/developers"
              className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm text-gray-700 hover:text-wayja-teal"
            >
              <Zap className="w-4 h-4" />
              <span>Portal</span>
            </Link>
          </div>
        </div>

        {/* Commission Rate Info Card */}
        {commissionRate !== null && (
          <div className="bg-gradient-to-r from-wayja-teal to-wayja-dark-teal rounded-lg shadow-lg mb-6 p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold mb-1">Your Commission Rate</h3>
                <p className="text-wayja-teal-100 text-sm mb-2">
                  You earn {((commissionRate || 0) * 100).toFixed(2)}% of the service fee ({(settings.serviceFee * 100).toFixed(1)}% of each bet) = <strong>{((commissionRate || 0) * settings.serviceFee * 100).toFixed(2)}%</strong> of each bet amount
                </p>
                <p className="text-wayja-teal-100 text-xs">
                  Commission is calculated as a percentage of the service fee on all bets placed on markets you create. The service fee is configurable by administrators.
                </p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-bold">
                  {((commissionRate || 0) * 100).toFixed(2)}%
                </div>
                <div className="text-wayja-teal-100 text-sm mt-1">Commission</div>
              </div>
            </div>
          </div>
        )}

        {/* API Keys List */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Your API Keys</h2>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-wayja-teal" />
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-12">
              <Key className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">You don't have any API keys yet.</p>
              <Link
                to="/developers/register"
                className="inline-flex items-center px-4 py-2 bg-wayja-teal text-white rounded-lg hover:bg-wayja-dark-teal"
              >
                <Plus className="w-5 h-5 mr-2" />
                Generate Your First API Key
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {apiKeys.map((key) => (
                <div
                  key={key.id}
                  className={`p-6 hover:bg-gray-50 transition-colors ${
                    selectedKeyId === key.id ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{key.key_name}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          key.environment === 'production' 
                            ? 'bg-purple-100 text-purple-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {key.environment}
                        </span>
                        {key.is_active ? (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Active
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 flex items-center gap-1">
                            <XCircle className="w-3 h-3" />
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
                        <div>
                          <div className="text-gray-500">Total Requests</div>
                          <div className="font-semibold text-gray-900">{key.total_requests.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Last 30 Days</div>
                          <div className="font-semibold text-gray-900">{key.requests_last_30_days.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Created</div>
                          <div className="font-semibold text-gray-900">{formatDate(key.created_at)}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Last Used</div>
                          <div className="font-semibold text-gray-900">{formatDate(key.last_request_at)}</div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => {
                          if (selectedKeyId === key.id) {
                            setSelectedKeyId(null);
                            setUsageStats(null);
                          } else {
                            setSelectedKeyId(key.id);
                            fetchUsageStats(key.id);
                          }
                        }}
                        className={`p-2 rounded-lg ${
                          selectedKeyId === key.id
                            ? 'bg-wayja-teal text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        title="View Statistics"
                      >
                        <Activity className="w-5 h-5" />
                      </button>
                      {key.is_active && (
                        <button
                          onClick={() => handleRevokeClick(key.id)}
                          className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200"
                          title="Revoke Key"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Usage Statistics */}
        {selectedKeyId && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Usage Statistics (Last 30 Days)</h2>
            </div>
            {loadingStats ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-wayja-teal" />
              </div>
            ) : usageStats ? (
              <div className="p-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Activity className="w-5 h-5 text-blue-600" />
                      <div className="text-sm text-blue-600 font-medium">Total Requests</div>
                    </div>
                    <div className="text-2xl font-bold text-blue-900">
                      {usageStats.total_requests.toLocaleString()}
                    </div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <div className="text-sm text-green-600 font-medium">Successful</div>
                    </div>
                    <div className="text-2xl font-bold text-green-900">
                      {usageStats.successful_requests.toLocaleString()}
                    </div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <XCircle className="w-5 h-5 text-red-600" />
                      <div className="text-sm text-red-600 font-medium">Failed</div>
                    </div>
                    <div className="text-2xl font-bold text-red-900">
                      {usageStats.failed_requests.toLocaleString()}
                    </div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="w-5 h-5 text-purple-600" />
                      <div className="text-sm text-purple-600 font-medium">Avg Response</div>
                    </div>
                    <div className="text-2xl font-bold text-purple-900">
                      {Math.round(usageStats.avg_response_time_ms || 0)}ms
                    </div>
                  </div>
                </div>

                {/* Data Transfer */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Server className="w-5 h-5 text-gray-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Data Transfer</h3>
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {formatBytes(usageStats.total_data_transferred_bytes || 0)}
                  </div>
                </div>

                {/* Requests by Endpoint */}
                {usageStats.requests_by_endpoint && usageStats.requests_by_endpoint.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Requests by Endpoint</h3>
                    <div className="space-y-2">
                      {usageStats.requests_by_endpoint.map((endpoint, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <div className="font-medium text-gray-900">{endpoint.endpoint}</div>
                            <div className="text-sm text-gray-500">
                              Avg: {Math.round(endpoint.avg_response_time_ms || 0)}ms
                            </div>
                          </div>
                          <div className="text-lg font-semibold text-gray-900">
                            {endpoint.count.toLocaleString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No usage statistics available for this period</p>
              </div>
            )}
          </div>
        )}

        {/* Quick Links */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/developers/api-reference"
            className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow"
          >
            <h3 className="font-semibold text-gray-900 mb-2">API Reference</h3>
            <p className="text-sm text-gray-600">View complete API documentation</p>
          </Link>
          <Link
            to="/developers/quickstart"
            className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow"
          >
            <h3 className="font-semibold text-gray-900 mb-2">Quick Start Guide</h3>
            <p className="text-sm text-gray-600">Get started with the API</p>
          </Link>
          <Link
            to="/developers/examples"
            className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow"
          >
            <h3 className="font-semibold text-gray-900 mb-2">Code Examples</h3>
            <p className="text-sm text-gray-600">Sample code in multiple languages</p>
          </Link>
        </div>
      </div>

      {/* Confirmation Modal for Revoking API Key */}
      <ConfirmationModal
        isOpen={keyToRevoke !== null}
        onClose={() => setKeyToRevoke(null)}
        onConfirm={revokeApiKey}
        title="Revoke API Key"
        message={
          <div className="space-y-2">
            <p>Are you sure you want to revoke this API key?</p>
            <p className="text-sm text-gray-600">
              This action cannot be undone. The API key will be immediately deactivated and all requests using it will fail.
            </p>
          </div>
        }
        confirmText="Revoke Key"
        cancelText="Cancel"
        isLoading={isRevoking}
        variant="danger"
      />
    </div>
  );
}

